import { get, forEach, keyBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { ENTITY_UNITS } from '@commons/constants/units';
import utilsXLS, { EXPORT_DECIMALS_COUNT } from '@commons/utils/makeXLS';

/**
 * When fetching SupplierProducts, they include a property 'conversions'
 *
 * @param {Array} conversions an array of objects with the following structure:
 * [
 *  {
 *    id - ignored
 *    supplierProductPackagingId  - ignored
 *    convertedUnit - either 'kg', 'L' or 'unit'
 *    convertedQuantity - the quantity of the unit
 *  }
 * ...
 * ]
 * When a conversion does not exist for a given unit, the value is 0
 *
 * @returns {String} a string like this:  (<value_in_kg>;<value_in_L>;<value_in_unit>)
 */
const formatConversions = (conversions) => {
  const keyByConvertedUnit = keyBy(conversions, 'convertedUnit');
  return `${keyByConvertedUnit[ENTITY_UNITS.KILOGRAM]?.convertedQuantity || 0};${
    keyByConvertedUnit[ENTITY_UNITS.LITER]?.convertedQuantity || 0
  };${keyByConvertedUnit[ENTITY_UNITS.UNIT]?.convertedQuantity || 0}`;
};

/**
 * Generate columns settings to display in the main sheet of the xls file
 * matching some rules to apply on value formatting and associated key name property
 *
 * @return {Array} the list of columns settings that should appear on export file
 */
export function getMainSheetColumnsSettings(supplierProducts) {
  const columns = [
    {
      name: i18next.t('GENERAL.NAME'),
      propertyKey: 'name',
      transform: (value) => (value ? value.trim() : ''),
    },
    {
      name: 'SKU',
      propertyKey: 'sku',
      transform: (value) => (value ? value.trim() : i18next.t('GENERAL.SELECT_UNDEFINED')),
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.EAN_CODE'),
      propertyKey: 'ean',
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_SUPPLIER'),
      propertyKey: 'supplier.name',
      transform: (value) => (value ? value.trim() : ''),
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_MANUFACTURER_NAME'),
      propertyKey: 'manufacturerName',
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_INGREDIENT'),
      propertyKey: 'entityName',
      transform: (value) => (value ? value.trim() : i18next.t('GENERAL.SELECT_NONE_MASCULINE')),
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_INGREDIENT_ASSOCIATED_ON'),
      propertyKey: 'entityAssociatedAt',
      transform: (value) =>
        value && moment(value).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_CATEGORY'),
      propertyKey: 'category',
      transform: (value) => (value ? value.trim() : i18next.t('GENERAL.SELECT_NONE_FEMININE')),
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_SUB_CATEGORY'),
      propertyKey: 'subCategory',
      transform: (value) => (value ? value.trim() : i18next.t('GENERAL.SELECT_NONE_FEMININE')),
    },
    {
      type: 'currency',
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_PRICE'),
      propertyKey: 'price',
      decimals: EXPORT_DECIMALS_COUNT,
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_VAT_RATE'),
      propertyKey: 'vatRate',
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_CONVERSIONS'),
      propertyKey: 'conversions',
      transform: formatConversions,
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_RECIPES_UNIT'),
      propertyKey: 'packagings[0].unit',
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_CONDITIONNING_PACKAGING_WEIGHT'),
      propertyKey: 'packagingWeight',
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_LOSS'),
      propertyKey: 'loss',
    },
    {
      name: i18next.t('GENERAL.ALLERGENS'),
      propertyKey: 'allergens',
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_DETAILED_COMPOSITION'),
      propertyKey: 'detailedComposition',
    },
    {
      name: i18next.t('GENERAL.PER_100G_ENERGY'),
      propertyKey: 'energy',
    },
    {
      name: i18next.t('GENERAL.PER_100G_FAT'),
      propertyKey: 'fat',
    },
    {
      name: i18next.t('GENERAL.PER_100G_SATURATED_FAT'),
      propertyKey: 'saturatedFat',
    },
    {
      name: i18next.t('GENERAL.PER_100G_CARBOHYDRATES'),
      propertyKey: 'carbohydrates',
    },
    {
      name: i18next.t('GENERAL.PER_100G_SUGAR'),
      propertyKey: 'sugar',
    },
    {
      name: i18next.t('GENERAL.PER_100G_FIBER'),
      propertyKey: 'fiber',
    },
    {
      name: i18next.t('GENERAL.PER_100G_PROTEIN'),
      propertyKey: 'protein',
    },
    {
      name: i18next.t('GENERAL.PER_100G_SALT'),
      propertyKey: 'salt',
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_STATUS'),
      propertyKey: 'active',
      transform: (value) => i18next.t(value ? 'GENERAL.ACTIVE' : 'GENERAL.INACTIVE'),
    },
    {
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.SPECIFICATION_SHEET'),
      propertyKey: 'attachmentLink',
      transform: (value) => i18next.t(value ? 'GENERAL.YES' : 'GENERAL.NO'),
    },
  ];

  let numberOfSupplierInputs = 0;

  supplierProducts.forEach((supplierProduct) => {
    if (get(supplierProduct, 'supplierPriceInputs.length', 0) > numberOfSupplierInputs) {
      numberOfSupplierInputs = supplierProduct.supplierPriceInputs.length;
    }
  });

  for (let index = 0; index < numberOfSupplierInputs; index++) {
    columns.splice(9, 0, {
      propertyKey: `inputPriceName${index + 2}`,
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.PRICE_NAME', { value: index + 2 }),
    });
    columns.splice(10, 0, {
      type: 'currency',
      propertyKey: `price${index + 2}`,
      name: i18next.t('ADMIN.SUPPLIER_PRODUCTS.PRICE', { value: index + 2 }),
      decimals: EXPORT_DECIMALS_COUNT,
    });
  }

  return columns;
}

/**
 * Generate columns settings to display in the contect sheet of the xls file
 * matching some rules to apply on value formatting and associated key name property
 *
 * @return {Array} the list of columns settings that should appear on export file
 */
export function getContextSheetColumnsSettings() {
  return [
    {
      displayName: i18next.t('GENERAL.NAME'),
    },
    {
      displayName: 'SKU',
    },
    {
      displayName: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_SUPPLIER'),
    },
    {
      displayName: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_CATEGORY'),
    },
    {
      displayName: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_SUB_CATEGORY'),
    },
    {
      displayName: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_PACKAGING_NAME'),
    },
    {
      displayName: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_PACKAGING_VALUE'),
    },
    {
      displayName: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_PACKAGING_UNIT'),
    },
    {
      displayName: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_ORDER_PACKAGING'),
    },
    {
      displayName: i18next.t('ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_INVOICE_PACKAGING'),
    },
    {
      displayName: i18next.t(
        'ADMIN.SUPPLIER_PRODUCTS.LIST_COLUMN_PACKAGING_AVAILABLE_TO_INVENTORY',
      ),
    },
  ];
}

/**
 * Create context sheet information with its content to feed
 * (store name, supplier name, start date, end date)
 *
 * @param {Array<SupplierProduct>} supplierProducts - an appropriately formatted array of SupplierProducts
 *
 * @returns {void}
 */
export function createContextSheet(supplierProducts) {
  const headersSettings = getContextSheetColumnsSettings();

  const headers = headersSettings.map(({ displayName }) => displayName);

  let mappedData = [];

  forEach(supplierProducts, (supplierProduct) => {
    let lastPackagingName = null;
    forEach(supplierProduct.packagings, (packaging) => {
      mappedData.push([
        supplierProduct.name,
        supplierProduct.sku,
        (supplierProduct.supplier || {}).name,
        supplierProduct.category,
        supplierProduct.subCategory,
        packaging.name,
        packaging.quantity,
        lastPackagingName ? lastPackagingName : supplierProduct.packagingUnit,
        packaging.isUsedInOrder ? i18next.t('GENERAL.YES') : i18next.t('GENERAL.NO'),
        packaging.isUsedInInvoice ? i18next.t('GENERAL.YES') : i18next.t('GENERAL.NO'),
        packaging.isUsedInStock ? i18next.t('GENERAL.YES') : i18next.t('GENERAL.NO'),
      ]);
      lastPackagingName = packaging.name;
    });
  });

  return {
    title: i18next.t('ADMIN.SUPPLIER_PRODUCTS.EXPORT_PACKAGING_SHEET_NAME'),
    headers,
    data: mappedData,
  };
}

/**
 * Handle the export of the analytics reference data into a XLS file
 *
 * @returns {void}
 */
export function exportSupplierProducts(supplierProducts, currency) {
  if (!supplierProducts) {
    return;
  }

  const mainSheet = utilsXLS.generateDefaultSheet(
    i18next.t('ADMIN.SUPPLIER_PRODUCTS.EXPORT_SUPPLIER_PRODUCT_SHEET_NAME'),
    getMainSheetColumnsSettings(supplierProducts),
    supplierProducts,
    currency,
  );

  const contextSheet = createContextSheet(supplierProducts);

  utilsXLS.makeXLS(i18next.t('ADMIN.SUPPLIER_PRODUCTS.EXPORT_FILE_NAME'), [
    mainSheet,
    contextSheet,
  ]);
}
